import { isCardMenuAvailable } from "src/app/business/services-card/services-card.component";
import { ExternalSettings } from "../external-settings/external-settings.interface";
import { InternalSettings, PermissionList } from "../internal-settings/internal-settings.interface";
import { hasAllPermissions, hasOnePermission, hasPermission } from "../internal-settings/internal-settings.service";
import { OrderService } from "../order/order.service";
import { LocalStorageService } from "src/app/system/utility/local-storage.service";
import { BiometricService } from "src/app/system/utility/biometric.service";
import { PAActivity } from "./pa-menu-meta";
import { PABaseUtility } from "./pa-base.utility";
import { UserService } from "../user/user.service";
import { PRIVATE_ROUTE_PATH } from "src/app/system/core/private.routes";
import { SidebarActivityType } from "../../components/sidebar/sidebar";
import { PlatformService } from "src/app/system/utility/platform.service";

export class PAActivityUtility extends PABaseUtility {

  public static buildAccountHistory(index: number): PAActivity {
    return new PAActivity('accountHistory', 'LABEL_ACCOUNT_LIST', '/dashboard', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) => {
        if (UserService.isCorporateAgent(is, es)) return false;
        return hasOnePermission([PermissionList.ACCESS_ACCOUNT_LISTING], is.permissions);
      },
      subUrls: ['/pending-transactions', '/account-history', '/account-history/detail',
        '/account-history/memo-post-detail', '/account-history/voucher-list', '/account-history/check',
        '/account/floats', '/account/holds'],
    });
  }


  public static buildDownloadHistory(index: number): PAActivity {
    return new PAActivity('downloadHistory', 'LABEL_DOWNLOAD_HISTORY', '/download-history', index, {
      permissions: [PermissionList.ACCESS_WRITER_HISTORY_DOWNLOAD, PermissionList.ACCESS_WRITER_HISTORY_EMAIL],
      checkOnePermission: true,
    });
  }

  public static buildDownloadStatement(index: number): PAActivity {
    return new PAActivity('downloadStatement', 'LABEL_DOWNLOAD_STATEMENT', '/download-statement', index, {
      permissions: [PermissionList.ACCESS_EXTERNAL_STATEMENTS],
    });
  }

  public static buildViewMaintenance(index: number): PAActivity {
    return new PAActivity('viewMaintenance', 'LABEL_VIEW_MAINTENANCE', '/view-maintenance', index, {
      permissions: [PermissionList.ACCESS_ACCOUNT_VIEW_MAINTENANCE],
    });
  }

  public static buildScheduledTransactions(index: number): PAActivity {
    return new PAActivity('scheduleTransactions', 'SCHEDULED_TRANSACTIONS.SCHEDULED_TRANSACTIONS_TITLE', '/scheduled-transactions', index, {
      permissions: [PermissionList.ACCESS_SCHEDULED_PAYMENTS],
    });
  }

  public static buildApplyNewProducts(index: number): PAActivity {
    return new PAActivity('applyNewProducts', 'LABEL_APPLY_NEW', '/redirect-to-new-products', index, {
      permissions: [PermissionList.MOBIBRANCH_CUSTOMER_ACCESS],
    });
  }

  public static buildInternalTransfer(index: number): PAActivity {
    return new PAActivity('internalTransfer', 'LABEL_TRANSFER', '/internal-transfer', index, {
      permissions: [PermissionList.ACCESS_INTERNAL_TRANSACTION],
    });
  }

  public static buildTransfersByText(index: number): PAActivity {
    return new PAActivity('transfersByText', 'LABEL_TRANSFER_BY_TEXT', '/transfers-by-text', index, {
      permissions: [PermissionList.ACCESS_INTERNAL_TRANSACTION_BY_TEXT_ACCESS],
      subUrls: ['/transfers-by-text-add', '/transfers-by-text-update'],
    });
  }

  public static buildCrossCurrencyTransfer(index: number): PAActivity {
    return new PAActivity('crossCurrencyTransfer', 'LABEL_CROSS_CURRENCY_TRANSFER', '/cross-currency-transfer', index, {
      permissions: [PermissionList.ACCESS_CROSS_CURRENCY_TRANSACTION],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledCrossCurrency,
    });
  }

  public static buildTopupReports(index: number): PAActivity {
    return new PAActivity('topupReports', 'LABEL_MOBILEARTH_TOPUP_REPORTS', '/payment/report', index, {
      permissions: [PermissionList.MOBILEARTH_ACCESS_TOPUP_SUBMITTED_REPORT],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledTopupServer,
    });
  }

  public static buildPaymentTopup(index: number): PAActivity {
    return new PAActivity('topup', 'LABEL_MOBILEARTH_TOPUP_PAYMENT', '/payment/topup', index, {
      permissions: [PermissionList.MOBILEARTH_ACCESS_MAKE_PAYMENT_MERCHANT, PermissionList.ACCESS_MOBILEARTH_TOPUP],
    });
  }

  public static buildPaymentGeneral(index: number): PAActivity {
    return new PAActivity('general', 'LABEL_MAKE_A_PAYMENT', '/payment/general', index, {
      checkOnePermission: true,
      permissions: [PermissionList.MOBILEARTH_ACCESS_MAKE_PAYMENT_MERCHANT, PermissionList.MOBILEARTH_ACCESS_MAKE_PAYMENT_PEER],
    });
  }

  public static buildPaymentDomestic(index: number): PAActivity {
    return new PAActivity('domestic', 'DOMESTIC_PAYMENTS', '/payment/domestic', index, {
      permissions: [PermissionList.PERFORM_ONLINE_DOMESTIC_PAYMENT],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledDomesticPayments,
    });
  }

  public static buildPaymentExternal(index: number): PAActivity {
    return new PAActivity('external', 'EXTERNAL_PAYMENTS', '/payment/external', index, {
      permissions: [PermissionList.ACCESS_BILL_PAY_PERFORM_MAINTAIN_MERCHANT],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledCreditUnionPayment,
    });
  }

  public static buildManagePayees(index: number): PAActivity {
    return new PAActivity('managePayees', 'ACCOUNT_VIEW_COMMON_EDIT_ALIAS', '/manage-payees', index, {
      checkOnePermission: true,
      permissions: [PermissionList.ACCESS_BILL_PAY_PERFORM_MAINTAIN_MERCHANT, PermissionList.ACCESS_BUDDY_LIST, PermissionList.MANAGE_PAYEES],
      subUrls: ['/manage-payees/add-peers', '/manage-payees/delete-peers', '/manage-payees/update-peers',
        '/manage-payees/add-merchants', '/manage-payees/delete-merchants', '/manage-payees/update-merchants',
        '/manage-payees/add-topup-recipients', '/manage-payees/delete-topup-recipients', '/manage-payees/update-topup-recipients',
        '/manage-payees/add-external-payees', '/manage-payees/delete-external-payees', '/manage-payees/update-external-payees',
        '/manage-payees/add-domestic-payees', '/manage-payees/delete-domestic-payees', '/manage-payees/update-domestic-payees'],
    });
  }

  public static buildPaymentHistory(index: number): PAActivity {
    return new PAActivity('paymentHistory', 'LABEL_PAYMENT_HISTORY', '/payment-history', index, {
      permissions: [PermissionList.ACCESS_BILL_PAY_PERFORM_HISTORY],
    });
  }

  public static buildPayrollDepartments(index: number): PAActivity {
    return new PAActivity('departments', 'PAYROLL_DEPARTMENTS_TITLE', '/payroll-department', index, {
      permissions: [PermissionList.PAYROLL_ACCESS_BATCHES_SCREEN],
      subUrls: ['/payroll-manage-members', '/payroll-manage-batches'],
    });
  }

  public static buildPayrollBatchUpload(index: number): PAActivity {
    return new PAActivity('batchUpload', 'PAYROLL_UPLOAD_TITLE', '/payroll-batch-upload', index, {
      permissions: [PermissionList.PAYROLL_UPLOAD_BATCHES],
    });
  }

  public static buildPayrollDepartmentGroups(index: number): PAActivity {
    return new PAActivity('departmentGroups', 'PAYROLL_DEPARTMENT_GROUPS_TITLE', '/payroll-department-group', index, {
      permissions: [PermissionList.ACCESS_MANAGE_USER_GROUPS],
    });
  }

  public static buildOrdersBank(index: number): PAActivity {
    return new PAActivity('bank', 'BANK_TRANSFER', '/bank', index, {
      permissions: [PermissionList.CREATE_BANK_TRANSFER],
    });
  }

  public static buildOrdersBatchOrder(index: number): PAActivity {
    return new PAActivity('batchOrderUpload', 'BATCH_EXTERNAL_TRANSFERS', '/batch-order-upload', index, {
      permissions: [PermissionList.UPLOAD_BATCH_ORDERS, PermissionList.VIEW_BATCH_ORDER_SUMMARY],
      checkOnePermission: true,
      subUrls: ['/batch-order-manage'],
    });
  }

  public static buildOrdersCash(index: number): PAActivity {
    return new PAActivity('cash', 'ORDERS_CASH', '/cash', index, {
      permissions: [PermissionList.CREATE_CASH_ORDER],
    });
  }

  public static buildOrdersCheque(index: number): PAActivity {
    return new PAActivity('cheque', 'ORDERS_CHEQUE', '/cheque', index, {
      permissions: [PermissionList.CREATE_CHEQUE_ORDER],
    });
  }

  public static buildOrdersDraft(index: number): PAActivity {
    return new PAActivity('draft', 'DRAFT', '/draft', index, {
      permissions: [PermissionList.CREATE_DRAFT_ORDER],
    });
  }

  public static buildOrdersDraftAutomated(index: number): PAActivity {
    return new PAActivity('draftAutomated', 'DRAFT', '/draftAutomated', index, {
      permissions: [PermissionList.CREATE_DRAFT_AUTOMATED_ORDER],
    });
  }

  public static buildOrdersEft(index: number): PAActivity {
    return new PAActivity('eft', 'EFT_ORDER', '/eft', index, {
      permissions: [PermissionList.CREATE_EFT_ORDER],
    });
  }

  public static buildOrdersWire(index: number): PAActivity {
    return new PAActivity('wire', 'ORDERS_WIRE_TRANSFER', '/wire', index, {
      permissions: [PermissionList.CREATE_WIRE_ORDER_STANDARD],
    });
  }

  public static buildOrdersHistory(index: number): PAActivity {
    return new PAActivity('orderHistory', 'ORDERS_HISTORY', '/order-history', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) => {
        return hasAllPermissions([PermissionList.LIST_ORDERS], is.permissions) && hasOnePermission(
          [
            PermissionList.CREATE_CASH_ORDER,
            PermissionList.CREATE_CHEQUE_ORDER,
          ], is.permissions
        )
      }
    });
  }

  public static buildExternalTransfersHistory(index: number): PAActivity {
    return new PAActivity('etHistory', 'LABEL_EXTERNAL_TRANSFER_HISTORY', '/external-transfer-history', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) => {
        return hasAllPermissions([PermissionList.LIST_ORDERS], is.permissions) && hasOnePermission(
          [
            PermissionList.CREATE_DRAFT_ORDER,
            PermissionList.CREATE_DRAFT_AUTOMATED_ORDER,
            PermissionList.CREATE_EFT_ORDER,
            PermissionList.CREATE_WIRE_ORDER_STANDARD
          ], is.permissions
        )
      }
    });
  }

  public static buildOrdersScheduledOrders(index: number): PAActivity {
    return new PAActivity('scheduledOrders', 'LABEL_SCHEDULED_EXTERNAL_TRANSFERS', '/scheduled-orders', index, {
      permissions: [PermissionList.SCHEDULE_ORDERS],
    });
  }

  public static buildOrdersTemplate(index: number): PAActivity {
    return new PAActivity('templates', 'ORDERS_TEMPLATES.TITLE', '/templates', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) => OrderService.isAllowTemplateMenu(is, es),
      subUrls: ['/templates/create', '/templates/edit'],
    });
  }

  public static buildVizSendMoney(index: number): PAActivity {
    return new PAActivity('vizSendMoney', 'VIZ.SEND_MONEY', '/viz-send-money', index, {
      permissions: [ PermissionList.ACCESS_MAKE_VIZ_SEND_MONEY_TRANSACTION ],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledVizSendMoney,
      subUrls: [
        '/viz-history', 
        '/viz-settings',
        '/viz-payee-add', 
        '/viz-payee-delete', 
        '/viz-payee-update'],
      type: SidebarActivityType.VIZ
    });
  }

  public static buildQRCodePaymentScan(index: number): PAActivity {
    return new PAActivity('qrCodePaymentScan', 'QR_PAYMENT.SCAN', '/qr-payment', index, {
      permissions: [PermissionList.SCAN_PEER_QR_CODE, PermissionList.SCAN_MERCHANT_QR_CODE],
      checkOnePermission: true,
    });
  }

  public static buildQRCodePaymentRequest(index: number): PAActivity {
    return new PAActivity('qrCodePaymentRequest', 'QR_PAYMENT.REQUEST', '/qr-create', index, {
      permissions: [PermissionList.CREATE_PEER_QR_CODE, PermissionList.CREATE_ORDER_QR_CODE],
      checkOnePermission: true,      
    });
  }

  public static buildServicesApplications(index: number): PAActivity {
    return new PAActivity('applications', 'LITE_APPLICATIONS.APPLICATIONS', '/services/applications', index, {
      permissions: [PermissionList.ACCESS_LITE_APPLICATIONS],
      subUrls: ['/services/applications-create', '/services/applications/new-account', '/services/applications/personal-loan',
        '/services/applications/self-registration', '/services/applications/car-second', '/services/applications/mortgage-cash',
        '/services/applications/emergency-psa', '/services/applications/personal-school'],
    });
  }

  public static buildServicesCard(index: number): PAActivity {
    return new PAActivity('card', 'CARD', '/services/card', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) => isCardMenuAvailable(is, es),
      subUrls: ['/services/card/change-address', '/services/card/report-lost',
        '/services/card/manage-debit-card', '/services/card/debit-card-reset-pin', '/services/card/debit-card-change-pin',
        '/services/card/manage-credit-card', '/services/card/credit-card-reset-pin', '/services/card/credit-card-change-pin'],
    });
  }

  public static buildServicesRequest(index: number): PAActivity {
    return new PAActivity('request', 'SERVICE_REQUEST.SERVICE_REQUEST_LABEL', '/services/request', index, {
      permissions: [PermissionList.VIEW_SERVICE_REQUEST],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledMobibranchRequest,
    });
  }

  public static buildServicesSmartForms(index: number): PAActivity {
    return new PAActivity('smartForms', 'SMART_FORMS.MENU', '/services/smart-forms', index, {
      permissions: [PermissionList.VIEW_SMART_FORMS],
      isModuleEnabled: !!this.es.globalVariables.isModuleEnabledMobibranchSmartForms,
    });
  }

  public static buildServicesSecureMessage(index: number): PAActivity {
    return new PAActivity('secureMessage', 'SECURE_MESSAGE.SECURE_MESSAGE_TITLE', '/secure-message', index, {
      permissions: [PermissionList.SECURE_MESSAGE],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledSecureMessage,
    });
  }

  public static buildServicesStopPayment(index: number): PAActivity {
    return new PAActivity('stopPayment', 'STOP_PAYMENT.STOP_PAYMENT_TITLE', '/stop-payment', index, {
      permissions: [PermissionList.MOBILEARTH_ACCESS_CUSTOMER_STOP_PAYMENT_VIEW],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledStopPayment,
    });
  }

  public static buildServicesDocumentViewer(index: number): PAActivity {
    return new PAActivity('documentViewer', 'ORDERS_HISTORY', '/order-history', index, {
      permissions: [PermissionList.LIST_ORDERS],
    });
  }

  public static buildServicesScheduledTransactions(index: number): PAActivity {
    return new PAActivity('scheduleTransactions', 'SCHEDULED_TRANSACTIONS.SCHEDULED_TRANSACTIONS_TITLE', '/scheduled-transactions', index, {
      permissions: [PermissionList.ACCESS_SCHEDULED_PAYMENTS],
    });
  }

  public static buildSettingsAdmin(index: number): PAActivity {
    return new PAActivity('admin', 'SETTINGS_ADMIN', '/settings/admin', index, {
      permissions: [PermissionList.ACCESS_MANAGE_USER_ADMIN],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledUserAdmin,
      subUrls: [
        '/settings/admin/manage-payees',
        '/settings/admin/manage-payees/add-merchants',
        '/settings/admin/manage-payees/delete-merchants',
        '/settings/admin/manage-payees/update-merchants'],
    });
  }

  public static buildSettingsPersonal(index: number): PAActivity {
    return new PAActivity('personal', 'PERSONAL', '/settings/personal', index, {
      checkOnePermission: true,
      permissions: [
        PermissionList.ACCESS_PASSWORD_CHANGE,
        PermissionList.ACCESS_LANGUAGE_CHANGE,
        PermissionList.ACCESS_EMAIL_CHANGE,
        PermissionList.ACCESS_ID_CHANGE],
      subUrls: [
        '/settings/personal',
        '/settings/personal/alias',
        '/settings/personal/changePassword',
        '/settings/personal/changeEmail',
        '/settings/personal/mobileId',
        '/settings/personal/changeSecurityQuestion',
        '/settings/personal/viz-alias'],
    });
  }

  public static buildSettingsPreferences(index: number): PAActivity {
    return new PAActivity('perferences', 'LABEL_PREFERENCES', '/settings/preference', index, {
      checkOnePermission: true,
      permissions: [
        PermissionList.ACCESS_TIME_ZONE_CHANGE,
        PermissionList.ACCESS_TEXT_ME_CHANGE,
        PermissionList.ACCESS_SUBSCRIBE_CHANGE,
      ],
      subUrls: [
        '/settings/preference/timezone', 
        '/settings/preference/marketing',
        '/settings/preference/text-message', 
        '/settings/preference/language'],
    });
  }

  public static buildSettingsAlerts(index: number): PAActivity {
    return new PAActivity('alerts', 'LABEL_ALERTS', '/settings/alerts', index, {
      permissions: [PermissionList.ACCESS_ALERT],
    });
  }

  public static buildSettingsResetPush(index: number): PAActivity {
    return new PAActivity('resetPush', 'RESET_PUSH_TOKENS.MENU_TITLE', '/settings/reset-push', index, {
      isModuleEnabled: this.es.globalVariables.isModuleEnabledPushNotifications,
      checkFunction: (is: InternalSettings, es: ExternalSettings) =>
        PlatformService.isMobile() && hasPermission(PermissionList.RESET_PUSH_TOKENS, is.permissions),
    });
  }

  public static buildSettingsBiometrics(index: number): PAActivity {
    return new PAActivity('biometrics', 'LABEL_BIOMETRICS', '/biometrics', index, {
      permissions: [],
      checkFunction: () => PlatformService.isMobile(),
    });
  }

  public static buildHelpDesk(index: number): PAActivity {
    return new PAActivity('helpDesk', 'SUPPORT_CENTER.HELP_DESK', '/help-desk', index, {
      permissions: [PermissionList.ACCESS_ALERT, PermissionList.ACCESS_LIVE_CHAT],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledCustomerHelpDesk,
    });
  }

  public static buildContactUs(index: number): PAActivity {
    return new PAActivity('contactUs', 'LABEL_CONTACT_US', `/${PRIVATE_ROUTE_PATH}/contact-us`, index, {
      permissions: [PermissionList.ACCESS_ALERT],
    });
  }

  public static buildResourceViewer(index: number): PAActivity {
    return new PAActivity('resourceViewer', 'SUPPORT_CENTER.RESOURCES', '/resource-viewer', index, {
      permissions: [PermissionList.ACCESS_RESOURCES],
      isModuleEnabled: this.es.globalVariables.isModuleEnabledMobibranchFileVault,
    });
  }

  public static buildTools(index: number): PAActivity {
    return new PAActivity('tools', 'LABEL_TOOLS', '/tools', index);
  }

  public static buildExternalLink1(index: number): PAActivity | null {
    if (!this.is.externalLinks?.name1) return null;
    return new PAActivity('externalLink1', this.is.externalLinks?.name1, '/external-links/1', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) => (is.externalLinks?.name1 != null),
    });
  }

  public static buildExternalLink2(index: number): PAActivity | null {
    if (!this.is.externalLinks?.name2) return null;
    return new PAActivity('externalLink2', this.is.externalLinks?.name2, '/external-links/2', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) => (is.externalLinks?.name2 != null),
    });
  }


  public static buildCorpMakePayment(index: number): PAActivity {
    return new PAActivity('corpMakePayment', 'LABEL_MAKE_A_PAYMENT', '/corporate/make-payment', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) => UserService.isCorporateAgent(is, es),
    });
  }

  public static buildCorpPaymentHistory(index: number): PAActivity {
    return new PAActivity('corpPaymentHistory', 'LABEL_PAYMENT_HISTORY', '/corporate/payment-history', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) => UserService.isCorporateAgent(is, es),
    });
  }

  public static buildCorpManageCustomers(index: number): PAActivity {
    return new PAActivity('corpManageCustomers', 'LABEL_MANAGE_CUSTOMERS', '/corporate/manage-customer', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) => UserService.isCorporateAgent(is, es),
    });
  }

  public static buildBiometrics(index: number, srvLocalStorage: LocalStorageService, srvBiometric: BiometricService): PAActivity {
    return new PAActivity('biometrics', 'LABEL_BIOMETRICS', '/biometrics', index, {
      checkFunction: (is: InternalSettings, es: ExternalSettings) =>
        PlatformService.isMobile() && UserService.isBiometricSupport(srvLocalStorage, srvBiometric),
    });
  }
}